// ChatApp.jsx with socket.io integration
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Grid, CircularProgress, Box, Typography } from '@mui/material';
import ChatList from './ChatList.js';
import ChatPanel from './ChatPanel.js';
import { therapistClient } from '../../../../PatientApps/apiClient';
import io from 'socket.io-client'; // Import socket.io-client

const ChatAppPage = () => {
  // --- component state ---
  const [loading, setLoading] = useState(true);
  const [chatLoading, setChatLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  
  // Cache for storing messages by chat ID
  const [messageCache, setMessageCache] = useState({});
  // Cache for storing chat details by chat ID
  const [chatCache, setChatCache] = useState({});
  
  // Socket reference to maintain throughout component lifecycle
  const socketRef = useRef();
  
  // Initialize socket connection
  useEffect(() => {
    // Create socket connection
    const socket = io('https://care.ascnau.com' || 'http://localhost:5000', {
      withCredentials: true,
    });
    
    socket.on('connect', () => {
      console.log('Socket connected');
    });
    
    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });
    
    // Listen for new messages
    socket.on('new_message', (message) => {
      // Process the new message
      console.log('New message received:', message);
      const formattedMessage = {
        id: message.message_uuid,
        content: message.content,
        timestamp: new Date(message.created_at),
        sender: {
          id: message.patient_uuid, // Assuming this message is from the patient
          name: 'Patient', // You might want to get the actual name
          isCurrentUser: false,
        },
        status: message.status || 'received'
      };
      
      // Update messages and cache
      const chatId = message.chat_uuid;
      if (chatId === selectedChatId) {
        setMessages(prevMessages => [...prevMessages, formattedMessage]);
      }
      
      // Update the cache regardless of whether this chat is selected
      setMessageCache(prevCache => {
        const currentMessages = prevCache[chatId] || [];
        return {
          ...prevCache,
          [chatId]: [...currentMessages, formattedMessage]
        };
      });
      
      // Also update the unread count for this chat if it's not the selected one
      if (chatId !== selectedChatId) {
        const updatedChats = chats.map(chat => {
          if (chat.chatUuid === chatId) {
            return {
              ...chat,
              unreadCount: (chat.unreadCount || 0) + 1
            };
          }
          return chat;
        });
        setChats(updatedChats);
      }
    });
    
    // Handle message status updates
    socket.on('message_saved', (message) => {
      console.log('Message confirmed saved:', message);
      // If needed, update the message status in UI
    });
    
    // Handle errors
    socket.on('error', (error) => {
      console.error('Socket error:', error);
      // You might want to show an error message to the user
    });
    
    // Save the socket instance to the ref
    socketRef.current = socket;
    
    // Clean up on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [chats, selectedChatId]);
  
  // Join the appropriate chat room when a chat is selected
  useEffect(() => {
    if (!selectedChatId || !socketRef.current) return;
    
    console.log(`Joining chat room ${selectedChatId}`);
    socketRef.current.emit('join_chat', selectedChatId);
    
    // No need to leave rooms explicitly as socket.io handles this automatically
    // when joining a new room
  }, [selectedChatId]);
  
  // Load chats from API on initial render
  useEffect(() => {
    const fetchChats = async () => {
      try {
        setLoading(true);
        const response = await therapistClient.get('/api/protected/therapist/chat-users');
        const chatUsers = response.data;
        
        // Transform API data to match our component structure with proper null checks
        const formattedChats = Array.isArray(chatUsers) ? chatUsers.map(user => ({
          id: user?.id || '',
          chatUuid: user?.chat_uuid || '',
          name: user?.name || '',
          role: user?.role || 'patient',
          isOnline: user?.is_online || false,
          email: user?.email || '',
          phone: user?.phone_number || '',
          avatar: null,
          status: user?.is_online ? 'online' : 'offline'
        })) : [];
        
        setChats(formattedChats);
        
        // Select the first chat by default if available
        if (formattedChats.length > 0 && formattedChats[0].chatUuid) {
          setSelectedChatId(formattedChats[0].chatUuid);
        }
      } catch (err) {
        console.error('Failed to fetch chat users:', err);
        setError('Failed to load chats. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchChats();
  }, []);
  
  // Function to handle chat selection with caching
  const handleSelectChat = useCallback((chatId) => {
    setSelectedChatId(chatId);
    
    // If we have this chat's data in cache, use it immediately
    if (chatCache[chatId]) {
      setSelectedChat(chatCache[chatId]);
    }
    
    if (messageCache[chatId]) {
      setMessages(messageCache[chatId]);
    } else {
      // If not in cache, we'll load it in the useEffect below
    }
    
    // Reset unread count for this chat
    setChats(prevChats => 
      prevChats.map(chat => 
        chat.chatUuid === chatId 
          ? { ...chat, unreadCount: 0 }
          : chat
      )
    );
  }, [chatCache, messageCache]);
  
  // Load messages when selected chat changes
  useEffect(() => {
    const fetchMessages = async () => {
      if (!selectedChatId) return;
      
      // If we already have this chat's messages in the cache, don't reload
      if (messageCache[selectedChatId] && chatCache[selectedChatId]) {
        return;
      }
      
      try {
        setChatLoading(true);
        const msgResponse = await therapistClient.get(`/api/protected/therapist/chats/${selectedChatId}`);
        const chatData = msgResponse.data;
        
        // Mark messages as read when opening a chat
        await therapistClient.post(`/api/protected/therapist/chats/${selectedChatId}/read`);
        
        // Process chat data
        const chatInfo = {
          id: chatData.chat_uuid,
          name: chatData.name,
          isGroup: chatData.is_group,
          participants: chatData.participants,
          unreadCount: 0, // Set to 0 after marking as read
          status: 'online' // You might want to determine this dynamically
        };
        
        // Update chat cache
        setChatCache(prev => ({
          ...prev,
          [selectedChatId]: chatInfo
        }));
        
        setSelectedChat(chatInfo);
        
        // Transform messages to match our component structure
        const formattedMessages = chatData.messages.map(msg => {
          const isTherapist = msg.therapist_uuid !== null;
          return {
            id: msg.message_uuid,
            content: msg.content,
            timestamp: new Date(msg.created_at),
            sender: {
              id: isTherapist ? msg.therapist_uuid : msg.patient_uuid,
              name: isTherapist ? chatData.therpaist_name : chatData.participants[0]?.name || 'Patient',
              isCurrentUser: isTherapist,
            },
            status: msg.status || 'sent'
          };
        });
        
        // Update message cache
        setMessageCache(prev => ({
          ...prev,
          [selectedChatId]: formattedMessages
        }));
        
        setMessages(formattedMessages);
      } catch (err) {
        console.error('Failed to fetch chat messages:', err);
        setError('Failed to load messages. Please try again later.');
      } finally {
        setChatLoading(false);
      }
    };
    
    fetchMessages();
  }, [selectedChatId, messageCache, chatCache]);
  
  // Handle sending a new message
  const handleSendMessage = async (content) => {
    if (!content.trim() || !selectedChatId || !socketRef.current) return;
    
    // Get patient info from the selected chat
    const patient = selectedChat?.participants?.[0];
    if (!patient) {
      console.error('No patient found for this chat');
      return;
    }
    
    // Create a temporary message with pending status
    const tempMessageId = `temp-${Date.now()}`;
    const newMessage = {
      id: tempMessageId,
      content: content,
      sender: { 
        id: 'therapist', // This should be the actual therapist ID in production
        name: 'You',
        isCurrentUser: true 
      },
      timestamp: new Date(),
      status: 'sending',
    };
    
    // Add temporary message to the UI and to cache
    const updatedMessages = [...messages, newMessage];
    setMessages(updatedMessages);
    
    // Update the message cache
    setMessageCache(prev => ({
      ...prev,
      [selectedChatId]: updatedMessages
    }));
    
    try {
      // Send message via socket.io
      socketRef.current.emit('send_message', {
        chat_uuid: selectedChatId,
        patient_uuid: patient.uuid,
        content: content,
        message_type: 'text',
        is_group: selectedChat.isGroup || false
      });
      
      // The socket will emit 'message_saved' event when the message is saved
      // which we listen for above
    } catch (err) {
      console.error('Failed to send message:', err);
      // Update the temporary message to show failure in UI and cache
      const messagesWithError = updatedMessages.map(msg => 
        msg.id === tempMessageId ? { ...msg, status: 'failed' } : msg
      );
      
      setMessages(messagesWithError);
      setMessageCache(prev => ({
        ...prev,
        [selectedChatId]: messagesWithError
      }));
    }
  };

  // The rest of your component remains the same
  return (
    <Box sx={{ 
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Grid container sx={{ 
        height: '100%',
        overflow: 'hidden'
      }}>
        {/* Left pane - Chat List */}
        <Grid item xs={4} sx={{ height: '100%', overflow: 'hidden' }}>
          <ChatList 
            chats={chats}
            selectedChatId={selectedChatId}
            onSelectChat={handleSelectChat}
            loading={loading}
          />
        </Grid>
        
        {/* Right pane - Chat Panel */}
        <Grid item xs={8} sx={{ height: '100%', overflow: 'hidden' }}>
          {error ? (
            <Box 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 3,
                textAlign: 'center'
              }}
            >
              <Typography variant="h6" color="error" gutterBottom>
                {error}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please try again later or contact support if the problem persists.
              </Typography>
            </Box>
          ) : selectedChat ? (
            <ChatPanel 
              chat={selectedChat}
              messages={messages}
              onSendMessage={handleSendMessage}
              loading={chatLoading}
            />
          ) : (
            <Box 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 3,
                bgcolor: 'background.default'
              }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Select a patient to start chatting
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a patient from the list on the left
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ChatAppPage;